import { useCallback, useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { Button, Modal, variables } from '@hopskipdrive/react-components';

import { StatusLabel } from '../../../components/shared/StatusChip/StatusChip.styles';
import { useFlags } from '../../../contexts/LDContext';
import { useUserState } from '../../../contexts/UserStateContext';
import { convertMinutesToMilliseconds, formatSecondsToMinutesAndSeconds } from '../../../utils/helpers/util';

export const IdleTimer = (props) => {
  const flags = useFlags();
  const MINIMUM_IDLE_TIMEOUT = 3;
  const timeout = convertMinutesToMilliseconds(Math.max(flags.alertsIdleTimeoutInMinutes, MINIMUM_IDLE_TIMEOUT));
  const promptBeforeIdle = timeout - (timeout - convertMinutesToMilliseconds(2));

  const [remaining, setRemaining] = useState<number>(Math.ceil(timeout / 1000));
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const { handleUpdateUserStatus, setOfflineDialogOpen } = useUserState();

  const onIdle = () => {
    setDialogOpen(true);
  };

  const onActive = () => {
    setDialogOpen(false);
  };
  const onPrompt = () => {
    setDialogOpen(true);
  };

  const { getRemainingTime, activate } = useIdleTimer({
    onIdle,
    onActive,
    onPrompt,
    timeout,
    promptBeforeIdle,
    throttle: 500,
  });
  const urlSearchParams = new URLSearchParams(location.search);
  const idleDebugMode = urlSearchParams.get('idleDebugMode') === 'true' && process.env.NODE_ENV != 'production';

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.ceil(getRemainingTime() / 1000));
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const handleGoOfflineRef = useRef<() => void>(null!);

  handleGoOfflineRef.current = () => {
    setDialogOpen(false);
    handleUpdateUserStatus(props.user.id, 'offline');
    setOfflineDialogOpen(true);
  };

  const handleGoOffline = useCallback(() => handleGoOfflineRef.current(), []);

  const timeTillPrompt = Math.max(remaining - promptBeforeIdle / 1000, 0);
  const seconds = timeTillPrompt > 1 ? 'seconds' : 'second';

  useEffect(() => {
    if (remaining == 0) {
      handleGoOffline();
    }
  }, [remaining, handleGoOffline]);

  return (
    <>
      {idleDebugMode && (
        <>
          <h1>Idle Debugging</h1>
          <br />
          {timeTillPrompt > 0 && (
            <p>
              {timeTillPrompt} {seconds} until prompt
            </p>
          )}
        </>
      )}
      <Modal data-testid="idle-modal" maxWidth={'sm'} open={dialogOpen}>
        <Modal.Title>Are you still here?</Modal.Title>
        <Modal.Content>
          <StatusLabel label={formatSecondsToMinutesAndSeconds(remaining)} textColor={variables.color.secondary} />
          <p>You have been idle for a while. Do you want to stay online?</p>
        </Modal.Content>
        <Modal.Actions>
          <Button variant="text" onClick={handleGoOffline}>
            Go Offline
          </Button>
          <Button color="primary" autoFocus onClick={() => activate()}>
            Stay Online
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};
